import { __exportAll } from "./_virtual/_rolldown/runtime.js";
import { getCurrentFormatLocale, getCurrentTimeZone } from "./locale-format.js";
import { parse } from "date-fns";
//#region src/date-helper.ts
var date_helper_exports = /* @__PURE__ */ __exportAll({
	UNDEF: () => "1",
	calculateDueWeeks: () => calculateDueWeeks,
	calculateDurationInMinutes: () => calculateDurationInMinutes,
	formatDateTime: () => formatDateTime,
	formatISODate: () => formatISODate,
	formatMonth: () => formatMonth,
	formatRelativeDate: () => formatRelativeDate,
	formatShortDate: () => formatShortDate,
	formatToTime: () => formatToTime,
	getUTCFromDatepickerString: () => getUTCFromDatepickerString,
	industrialHoursToMinutes: () => industrialHoursToMinutes,
	minutesToIndustrialHours: () => minutesToIndustrialHours,
	toISODateTime: () => toISODateTime
});
/** Default value for undefined/invalid results */
var UNDEF = "1";
/**
* Calculates the number of weeks between billing date and due date.
*
* @param billingDate - The billing date (Date or string)
* @param dueDate - The due date (Date or string)
* @returns Number of weeks as string ("1", "2", "3", "4"), or UNDEF if out of range
*/
var calculateDueWeeks = (billingDate, dueDate) => {
	const billingDateObj = typeof billingDate === "string" ? new Date(billingDate) : billingDate;
	const dueDateObj = typeof dueDate === "string" ? new Date(dueDate) : dueDate;
	const MS_PER_WEEK = 6048e5;
	const diffInMs = dueDateObj.getTime() - billingDateObj.getTime();
	const weeks = Math.round(diffInMs / MS_PER_WEEK);
	if (weeks >= 1 && weeks <= 4) return weeks.toString();
	return "1";
};
/**
* Converts minutes to industrial hours (decimal hours).
*
* @param minutes - Number of minutes
* @returns Industrial hours with max 2 decimal places
*/
var minutesToIndustrialHours = (minutes) => {
	if (typeof minutes !== "number" || isNaN(minutes) || minutes <= 0) return 0;
	return Math.round(minutes / 60 * 100) / 100;
};
/**
* Converts industrial hours to minutes.
*
* @param hours - Industrial hours
* @returns Minutes (rounded)
*/
var industrialHoursToMinutes = (hours) => {
	if (typeof hours !== "number" || isNaN(hours) || hours <= 0) return 0;
	return Math.round(hours * 60);
};
/**
* Calculates duration in minutes between two time points.
*
* @param start - Start time (Date or ISO string)
* @param end - End time (Date or ISO string)
* @returns Duration in minutes, or 0 if invalid
*/
var calculateDurationInMinutes = (start, end) => {
	if (!start || !end) return 0;
	const startDate = typeof start === "string" ? new Date(start) : start;
	return ((typeof end === "string" ? new Date(end) : end).getTime() - startDate.getTime()) / 6e4;
};
/**
* Formats a date/time to a time string (HH:mm).
*
* @param date - Date value (Date, string, or number)
* @param emptyResult - Value to return if date is undefined. Defaults to "--"
* @returns Formatted time string
*/
var formatToTime = (date, emptyResult) => {
	let resultStr = emptyResult !== void 0 ? emptyResult : "--";
	try {
		if (date !== void 0 && date !== null) resultStr = (typeof date === "string" || typeof date === "number" ? new Date(date) : date).toLocaleTimeString(getCurrentFormatLocale(), {
			hour: "2-digit",
			minute: "2-digit",
			timeZone: getCurrentTimeZone()
		});
	} catch {}
	return resultStr;
};
/**
* Formats a date to a short date string (DD.MM.YYYY) with optional time.
*
* @param date - Date value (Date, string, or number)
* @param withTime - Whether to include time in the output
* @returns Formatted date string or "--" if undefined
*/
var formatShortDate = (date, withTime) => {
	let resultStr = "--";
	if (date) resultStr = (typeof date === "string" || typeof date === "number" ? new Date(date) : date).toLocaleDateString(getCurrentFormatLocale(), withTime ? {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		timeZone: getCurrentTimeZone()
	} : {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		timeZone: getCurrentTimeZone()
	});
	return resultStr;
};
/**
* Formats how far a date lies from today, in calendar-day granularity.
* Recent dates become a relative phrase in the current locale ("heute",
* "gestern", "vor 3 Tagen" / "today", "3 days ago"); anything further
* away than maxDays falls back to the absolute formatShortDate. Day
* boundaries use local time, so yesterday evening reads "gestern" even
* when less than 24h have passed.
*
* @param date - Date value (Date, string, or number)
* @param maxDays - Distance in days up to which the phrase is relative (default 30)
* @returns Relative phrase or short date string, "--" if undefined
*/
var formatRelativeDate = (date, maxDays = 30) => {
	if (!date) return "--";
	const usedDate = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
	const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	const days = Math.round((startOfDay(usedDate) - startOfDay(/* @__PURE__ */ new Date())) / 864e5);
	if (Math.abs(days) > maxDays) return formatShortDate(usedDate, false);
	return new Intl.RelativeTimeFormat(getCurrentFormatLocale(), { numeric: "auto" }).format(days, "day");
};
/**
* Combines a date object with a time string to create an ISO date-time string.
*
* @param dateObj - The date object
* @param timeStr - Time string in "HH:mm" format
* @returns ISO date-time string
*/
function toISODateTime(dateObj, timeStr) {
	const [hours, minutes] = timeStr.split(":").map(Number);
	const newDate = new Date(dateObj);
	newDate.setHours(hours, minutes, 0, 0);
	return newDate.toISOString();
}
/**
* Formats a date to a long date string with optional time.
*
* @param date - Date value (Date, string, or number)
* @param withTime - Whether to include time in the output
* @returns Formatted date string (e.g., "Montag, 20. Januar 2026") or "--" if undefined
*/
function formatDateTime(date, withTime) {
	let resultStr = "--";
	if (date) resultStr = (typeof date === "string" || typeof date === "number" ? new Date(date) : date).toLocaleDateString(getCurrentFormatLocale(), withTime ? {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		timeZone: getCurrentTimeZone()
	} : {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone: getCurrentTimeZone()
	});
	return resultStr;
}
/**
* Formats a date to show only the month (and optionally year).
*
* @param date - Date value (Date, string, or number)
* @param withYear - Whether to include the year
* @returns Formatted month string (e.g., "Januar" or "Januar 2026") or "--" if undefined
*/
var formatMonth = (date, withYear) => {
	let resultStr = "--";
	if (date) resultStr = (typeof date === "string" || typeof date === "number" ? new Date(date) : date).toLocaleDateString(getCurrentFormatLocale(), withYear ? {
		month: "long",
		year: "numeric",
		timeZone: getCurrentTimeZone()
	} : {
		month: "long",
		timeZone: getCurrentTimeZone()
	});
	return resultStr;
};
/**
* Formats a date to ISO date string (yyyy-MM-dd).
* Useful for HTML date inputs and datepicker min/max attributes.
*
* @param date - Date value (Date, string, or number)
* @returns ISO date string (e.g., "2026-01-31") or empty string if undefined
*/
var formatISODate = (date) => {
	if (!date) return "";
	const usedDate = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
	return `${usedDate.getFullYear()}-${String(usedDate.getMonth() + 1).padStart(2, "0")}-${String(usedDate.getDate()).padStart(2, "0")}`;
};
var dateFormatWithoutTime = "dd/MM/yyyy";
/**
* Parses a datepicker string (dd/MM/yyyy) and returns UTC string.
*
* @param dateString - Date string in dd/MM/yyyy format
* @returns UTC string representation
*/
var getUTCFromDatepickerString = (dateString) => {
	return parse(dateString, dateFormatWithoutTime, /* @__PURE__ */ new Date()).toUTCString();
};
//#endregion
export { UNDEF, calculateDueWeeks, calculateDurationInMinutes, date_helper_exports, formatDateTime, formatISODate, formatMonth, formatRelativeDate, formatShortDate, formatToTime, getUTCFromDatepickerString, industrialHoursToMinutes, minutesToIndustrialHours, toISODateTime };
